/**
 * Category taxonomy: maps every transaction category (Mint's standard set
 * plus this dataset's custom ones) to a top-level spending group.
 *
 * Chart color policy (dataviz): 9 fixed categorical slots, assigned to the
 * most significant spending groups. Color follows the group everywhere —
 * a filtered chart never repaints survivors. All remaining groups fold into
 * a gray "Other" in charts (they stay itemized in tables and tooltips).
 */

export const GROUPS = [
  'Food & Dining',
  'Shopping',
  'Home',
  'Auto & Transport',
  'Travel',
  'Bills & Utilities',
  'Kids',
  'Entertainment',
  'Personal Care',
  'Gifts & Donations',
  'Education',
  'Fees & Charges',
  'Taxes, Interest & Insurance',
  'Business',
  'Pets',
  'Cash & Checks',
  'Everything Else',
] as const

export type Group = (typeof GROUPS)[number]

/** The groups that own a categorical color slot (fixed, never reassigned). */
export const COLORED_GROUPS: Group[] = [
  'Food & Dining',
  'Shopping',
  'Home',
  'Auto & Transport',
  'Travel',
  'Bills & Utilities',
  'Kids',
  'Taxes, Interest & Insurance',
]

export const OTHER_COLOR = 'var(--series-other)'

/** Palette slot per coloured group, stated EXPLICITLY rather than derived from
 * position in COLORED_GROUPS. Colour follows the group, never its rank: when
 * Health & Fitness was retired (Aug 2026) an index-based mapping would have
 * slid Taxes from teal onto its neighbour's orange, silently repainting a group
 * nobody touched. Slot 8 is simply vacant now — that is fine and intended. */
const GROUP_SLOT: Partial<Record<Group, number>> = {
  'Food & Dining': 1,
  'Shopping': 2,
  'Home': 3,
  'Auto & Transport': 4,
  'Travel': 5,
  'Bills & Utilities': 6,
  'Kids': 7,
  'Taxes, Interest & Insurance': 9,
}

export function groupColor(group: string): string {
  const slot = GROUP_SLOT[group as Group]
  return slot ? `var(--series-${slot})` : OTHER_COLOR
}

/**
 * Category taxonomy (post-2026-07 standardization).
 *
 *   Tier 1 — the 18 GROUPS above (kept from the Mint era).
 *   Tier 2 — Empower's own category labels (what Cowork submits, what the app
 *            reports). EMPOWER_GROUP maps each to its group.
 *   Tier 3 — the finer Mint/app labels, preserved on each txn as `subcategory`
 *            and rolled up to a tier-2 label via MINT_TO_EMPOWER. Hidden: nothing
 *            in the app reads `subcategory`; it is kept as data only. The importer
 *            no longer invents tier-3 labels from a row's description (the last
 *            such guess, Kids Activities, was promoted to tier 2 in Oct 2026).
 *
 * groupOf() accepts either tier: a tier-2 Empower label resolves directly; a
 * legacy tier-3 label resolves through MINT_TO_EMPOWER first, so mixed data
 * (mid-migration) always lands in the right group.
 */

/** Tier-3 (fine Mint/app label) → tier-2 (Empower label). Identity for labels
 * already at tier 2 is implicit (groupOf falls back to the label itself). */
export const MINT_TO_EMPOWER: Record<string, string> = {
  "Restaurants": "Restaurants", "Delivery/Takeout": "Restaurants", "Fast Food": "Restaurants",
  "Dessert Cafes": "Restaurants", "Coffee Shops": "Restaurants", "Alcohol & Bars": "Restaurants",
  "Work Cafeteria": "Restaurants", "Sandwich Shops": "Restaurants", "Bus Boy / Tip": "Restaurants",
  "Food Delivery": "Restaurants", "Food & Dining": "Restaurants", "Groceries": "Groceries",
  "Shopping": "General Merchandise", "Clothing": "Clothing/Shoes", "Electronics & Software": "General Merchandise",
  "Furnishings": "General Merchandise", "Books": "General Merchandise", "Sporting Goods": "General Merchandise",
  "Household Supplies": "General Merchandise", "Home Goods": "General Merchandise", "Bedding/Linens": "General Merchandise",
  "Beauty Products": "General Merchandise", "Hobbies": "Hobbies & Classes", "Guitar Supplies": "Hobbies & Classes",
  "Recording Equipment": "Hobbies & Classes", "Video Games": "General Merchandise", "Photo Printing": "General Merchandise",
  "iTunes Store": "General Merchandise", "Machinery": "General Merchandise", "Mavis's Budget": "General Merchandise",
  "Home Improvement": "Home Improvement", "Home": "Home Improvement", "Home Services": "Home Maintenance",
  "Home Supplies": "Home Maintenance", "Storage & Moving": "Home Maintenance", "Lawn & Garden": "Lawn and Garden",
  "Mortgage & Rent": "Mortgages", "Gas & Fuel": "Gasoline/Fuel", "Parking": "Transit and Parking",
  "Public Transportation": "Transit and Parking", "Service & Parts": "Automotive", "Auto Payment": "Automotive",
  "Ride Share": "Transit and Parking", "Bus": "Transit and Parking", "Train": "Transit and Parking",
  "Tolls": "Transit and Parking", "Local Taxi / Uber": "Transit and Parking", "Auto Fees and Taxes": "Automotive",
  // 2026-07 recategorization: airfare/lodging tier-3 labels roll up to the
  // dedicated Flights/Hotels tier-2 categories, not the generic Travel bucket.
  "Traffic Tickets": "Automotive", "Auto & Transport": "Automotive", "Air Travel": "Flights",
  "Rental Car & Taxi": "Travel", "Hotel": "Hotels", "Vacation": "Travel", "Travel": "Travel",
  "Travel Agency Fees": "Travel", "Utilities": "Utilities", "Internet": "Utilities", "Mobile Phone": "Utilities",
  "Television": "Utilities", "Subscriptions": "Online Services", "Babysitter & Daycare": "Child/Dependent",
  "Doctor": "Healthcare/Medical", "Pharmacy": "Healthcare/Medical",
  "Eyecare": "Healthcare/Medical", "Dentist": "Healthcare/Medical", "Gym": "Healthcare/Medical",
  "Health & Fitness": "Healthcare/Medical", "Sports": "Healthcare/Medical", "Health Insurance": "Insurance",
  "Auto Insurance": "Insurance", "Home Insurance": "Insurance", "Life Insurance": "Insurance",
  "Entertainment": "Entertainment", "Movies & DVDs": "Entertainment", "Amusement": "Entertainment",
  "Arts": "Entertainment", "Music": "Entertainment", "Concerts/Shows": "Entertainment",
  "Sporting Events": "Entertainment", "Clubs": "Entertainment", "NRB": "Entertainment",
  "Personal Care": "Personal Care", "Hair": "Personal Care", "Laundry": "Personal Care",
  "Dry Cleaning/Tailor": "Personal Care", "Feminine Products": "Personal Care", "Gift": "Gifts",
  "Charity": "Charitable Giving", "Tithes and Offerings": "Charitable Giving", "Gifts & Donations": "Gifts",
  "Student Loan": "Education", "Tuition": "Education", "Books & Supplies": "Education", "Education": "Education",
  "ATM Fee": "Service Charges/Fees", "Service Fee": "Service Charges/Fees", "Bank Fee": "Service Charges/Fees",
  "Fees & Charges": "Service Charges/Fees", "Finance Charge": "Service Charges/Fees", "Late Fee": "Service Charges/Fees",
  "Trade Commissions": "Service Charges/Fees", "Management Fees": "Service Charges/Fees",
  "Application Fees": "Service Charges/Fees", "Financial": "Service Charges/Fees", "Taxes": "Taxes",
  // Federal Tax → Income Tax (a tax payment, expense nature). Tax refunds are
  // their own income category "Tax Refund" now (NOT a negative Income Tax), so
  // there is deliberately no "Tax Refund" tier-3 entry here. Tax Preparation
  // stays in the generic Taxes bucket.
  "Federal Tax": "Income Tax", "Tax Preparation": "Taxes", "Business Services": "Professional Services",
  "Graphic Design": "Professional Services", "Shipping": "Postage & Shipping", "Office Supplies": "Professional Services",
  "Printing": "Printing", "Legal": "Professional Services", "Notary Public": "Professional Services",
  "Cloud Hosting": "Online Services", "Domain Registration": "Online Services", "Pet Grooming": "Pet Care",
  "Transfer": "Transfers", "Credit Card Payment": "Credit Card Payments", "Investments": "Securities Trades",
  "Buy": "Securities Trades", "Sell": "Securities Trades", "Deposit": "Retirement Contributions",
  "Withdrawal": "Transfers", "Interest Income": "Interest", "Paycheck": "Paychecks/Salary", "Income": "Other Income",
  "Bonus": "Other Income", "Reimbursement": "Expense Reimbursement", "Dividend & Cap Gains": "Investment Income",
  "Rental Income": "Rental Income", "Returned Purchase": "Refunds & Reimbursements", "CC Cash Back": "Rewards",
  "Cash & ATM": "ATM/Cash", "Check": "Transfers", "Loans": "Loans", "Loan Payment": "Loans",
  // "Loan Interest" (a fine label, unused in the ledger) pointed at "Loans",
  // whose nature is Transfer — so interest would have counted as money moving
  // rather than money spent. Routed to the expense category instead.
  "Loan Principal": "Loans", "Loan Interest": "Loan Interest Paid", "Transfer for Cash Spending": "ATM/Cash",
  "Misc Expenses": "Other Expenses", "Uncategorized": "Uncategorized", "Hide from Budgets & Trends": "Uncategorized",
}

/** Tier-2 (Empower label) → tier-1 group. */
export const EMPOWER_GROUP: Record<string, Group> = {
  "Restaurants": "Food & Dining", "Groceries": "Food & Dining", "General Merchandise": "Shopping",
  "Clothing/Shoes": "Shopping", "Home Improvement": "Home",
  "Home Maintenance": "Home", "Lawn and Garden": "Home", "Mortgages": "Home", "Rent": "Home",
  "Gasoline/Fuel": "Auto & Transport", "Transit and Parking": "Auto & Transport", "Automotive": "Auto & Transport",
  "Travel": "Travel", "Flights": "Travel", "Hotels": "Travel",
  "Utilities": "Bills & Utilities", "Online Services": "Bills & Utilities",
  "Dues & Subscriptions": "Bills & Utilities", "Child/Dependent": "Kids", "Kids Tuition": "Kids",
  // Kids Activities was PROMOTED from a hidden tier-3 alias of Child/Dependent
  // to a tier-2 category of its own (Oct 2026): the kids' classes, lessons,
  // camps and rec programs, split out from childcare/school fees
  // (Child/Dependent) and from the adults' Hobbies & Classes. It is an
  // Empower CUSTOM category (same string there), passed through verbatim.
  "Kids Activities": "Kids",
  // Health & Fitness was retired Aug 2026: it only ever held these two, and
  // neither belonged. Medical spending is irregular and uninformative as its
  // own slice, so it sits with the uncoloured remainder; insurance is a
  // recurring obligation that buys no goods, so it groups with taxes/interest.
  "Healthcare/Medical": "Everything Else",
  "Insurance": "Taxes, Interest & Insurance", "Entertainment": "Entertainment", "Personal Care": "Personal Care",
  // Hobbies & Classes holds the ADULTS' fitness and movement classes (dance,
  // Orangetheory, pickleball) once the kids' rows moved to Kids Activities —
  // money spent on the self, not goods, so Personal Care rather than the Shopping
  // slot Mint gave it.
  "Hobbies & Classes": "Personal Care",
  "Gifts": "Gifts & Donations", "Charitable Giving": "Gifts & Donations", "Education": "Education",
  // Income Tax / Property Tax are Empower CUSTOM categories (2026-07
  // recategorization) — created by Cowork in Empower and passed through the
  // importer verbatim; the generic Taxes bucket remains for tax prep etc.
  "Service Charges/Fees": "Fees & Charges",
  "Taxes": "Taxes, Interest & Insurance", "Income Tax": "Taxes, Interest & Insurance",
  "Tax Refund": "Taxes, Interest & Insurance", "Property Tax": "Taxes, Interest & Insurance",
  // Interest PAID is an expense and belongs beside the other obligations —
  // previously unmapped, so it fell through to "Everything Else". (Interest
  // EARNED is the separate income-nature "Interest" category, left alone.)
  "Loan Interest Paid": "Taxes, Interest & Insurance",
  "Professional Services": "Business",
  "Postage & Shipping": "Business", "Printing": "Business", "Pet Care": "Pets", "ATM/Cash": "Cash & Checks",
  // non-spending tier-2 labels (kept out of spending groups; separated by rollup)
  "Other Expenses": "Everything Else", "Uncategorized": "Everything Else", "Transfers": "Everything Else",
  "Credit Card Payments": "Everything Else", "Securities Trades": "Everything Else",
  "Retirement Contributions": "Everything Else", "Interest": "Everything Else", "Paychecks/Salary": "Everything Else",
  "Other Income": "Everything Else", "Expense Reimbursement": "Everything Else", "Investment Income": "Everything Else",
  "Rental Income": "Everything Else", "Refunds & Reimbursements": "Everything Else", "Rewards": "Everything Else",
  "Loans": "Everything Else",
}

export function groupOf(category: string): Group {
  return EMPOWER_GROUP[category] ?? EMPOWER_GROUP[MINT_TO_EMPOWER[category]] ?? 'Everything Else'
}

/** A category's NATURE — the root of its path (Income / Expense / Transfer /
 * Investment). This is the single source of truth: a transaction's `rollup` is
 * now a materialized cache of `natureOf(category)`, never authored on its own,
 * so it can't drift from the category (the old bug where a "Mortgages" row was
 * stuck as a Transfer). Every category has EXACTLY ONE nature — there are no
 * dual categories; a name that needs both sides is two categories (Income Tax
 * for payments, Tax Refund for refunds). Direction within a nature is the
 * amount SIGN: a return in an expense category is negative spending, a clawback
 * in an income category is negative income — the nature never flips. */
export type Nature = 'Income' | 'Expense' | 'Transfer' | 'Investment'

/** Tier-2 labels whose nature is income. */
export const INCOME_TIER2 = new Set([
  'Paychecks/Salary',
  'Interest',
  'Investment Income',
  'Other Income',
  'Rental Income',
  'Expense Reimbursement',
  'Refunds & Reimbursements',
  'Rewards',
  'Tax Refund',
])

/** Tier-2 labels whose nature is transfer (money moving between own accounts —
 * never income or spending). */
export const TRANSFER_TIER2 = new Set(['Transfers', 'Credit Card Payments', 'Loans'])

/** Tier-2 labels whose nature is investment (securities/retirement flows —
 * never income or spending). */
export const INVESTMENT_TIER2 = new Set(['Securities Trades', 'Retirement Contributions'])

/** The nature of a category (either tier). Everything not income/transfer/
 * investment is expense — the default spending nature. */
export function natureOf(label: string): Nature {
  // Tier-2 label wins directly (a leaf like "Tax Refund" that also exists as a
  // tier-3 alias must resolve to its own nature, not the alias target's).
  if (INCOME_TIER2.has(label)) return 'Income'
  if (TRANSFER_TIER2.has(label)) return 'Transfer'
  if (INVESTMENT_TIER2.has(label)) return 'Investment'
  const c = MINT_TO_EMPOWER[label] ?? label
  if (INCOME_TIER2.has(c)) return 'Income'
  if (TRANSFER_TIER2.has(c)) return 'Transfer'
  if (INVESTMENT_TIER2.has(c)) return 'Investment'
  return 'Expense'
}

/** True when a label (either tier) is an income-nature category. */
export function isIncomeCategory(label: string): boolean {
  return natureOf(label) === 'Income'
}

/** True when a label resolves to a real group via either tier (i.e. groupOf
 * would NOT fall back to "Everything Else" by accident). */
export function isResolvableCategory(label: string): boolean {
  return label in EMPOWER_GROUP || label in MINT_TO_EMPOWER
}

// The two tiers are ~90 hand-typed strings each; a typo silently dumps
// transactions into "Everything Else". Validate the tier-3 → tier-2 edge at
// module load so a bad entry fails the first import/render loudly instead.
{
  const bad = [...new Set(Object.values(MINT_TO_EMPOWER).filter((v) => !(v in EMPOWER_GROUP)))]
  if (bad.length) {
    throw new Error(`categories.ts: MINT_TO_EMPOWER values with no EMPOWER_GROUP entry: ${bad.join(', ')}`)
  }
  // COLORED_GROUPS (membership + legend order) and GROUP_SLOT (which hue) are
  // two lists of the same set; a group in one but not the other renders either
  // as an uncoloured "Other" slice or with a colour no legend explains.
  const slotted = Object.keys(GROUP_SLOT)
  const missing = COLORED_GROUPS.filter((g) => !slotted.includes(g))
  const extra = slotted.filter((g) => !COLORED_GROUPS.includes(g as Group))
  if (missing.length || extra.length) {
    throw new Error(`categories.ts: COLORED_GROUPS/GROUP_SLOT disagree — no slot: [${missing}], no legend: [${extra}]`)
  }
}

/** Roll a fine (tier-3) Mint/app label up to its canonical tier-2 Empower label. */
export function toEmpowerLabel(fine: string): string {
  return MINT_TO_EMPOWER[fine] ?? fine
}

/* --------------------------------------------------------------------------
 * Account-type groups for balance/net-worth views.
 * Fixed color slots, same policy as spending groups.
 * ------------------------------------------------------------------------ */

export const ACCOUNT_GROUPS = [
  'Cash',
  'Investments',
  'Retirement',
  'Real Estate',
  'Education',
  'Credit Cards',
  'Loans',
  'Mortgages',
] as const

export type AccountGroup = (typeof ACCOUNT_GROUPS)[number]

const TYPE_TO_GROUP: Record<string, AccountGroup> = {
  Checking: 'Cash',
  Savings: 'Cash',
  Stock: 'Investments',
  Crypto: 'Investments',
  Custodial: 'Investments',
  HSA: 'Investments',
  DCFSA: 'Investments',
  Retirement: 'Retirement',
  'Real Estate': 'Real Estate',
  Education: 'Education',
  Credit: 'Credit Cards',
  Loan: 'Loans',
  Mortgage: 'Mortgages',
}

export function accountGroupOf(type: string): AccountGroup {
  return TYPE_TO_GROUP[type] ?? 'Investments'
}

export const LIABILITY_GROUPS: AccountGroup[] = ['Credit Cards', 'Loans', 'Mortgages']

export function accountGroupColor(group: AccountGroup): string {
  return `var(--series-${ACCOUNT_GROUPS.indexOf(group) + 1})`
}

/** URL-/anchor-safe slug for an account group (Overview links → Accounts anchors). */
export function accountGroupId(group: string): string {
  return group.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}
